'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useErrorDialog } from '@/shared/components/ui/ErrorDialog';
import TermsDialog from '@/shared/components/TermsDialog';
import { createAccount } from '@/shared/services/authServices';
import { REFERRAL_STORAGE_KEY } from '@/shared/services/referralsService';
import { onlyDigits, COUNTRIES, type Country, toE164, phonePlaceholder, formatPhone } from '@/shared/utils/phone';
import CountrySelector from '@/shared/components/CountrySelector';
import RecaptchaCheckbox, { type RecaptchaCheckboxHandle } from '@/shared/components/RecaptchaCheckbox';
import { isRecaptchaEnabled } from '@/shared/utils/recaptcha';
import { CaptchaModal, OtpInput, TermsCheck } from './CadastroParts';

const WHATSAPP_URL = 'https://chat.whatsapp.com/LFow5zbfZ2ZAfIREYUqo13';

type RegisterApiResponse = {
  // Backend pode responder em camelCase (CamelModel) ou PascalCase
  status?: string;
  Status?: string;
  message?: string;
  Message?: string;
  retryAfterSeconds?: number;
  remaining?: number;
};

type Step = 'form' | 'code' | 'success';

type RegisterForm = {
  name: string;
  loginId: string;
  password: string;
  confirmPassword: string;
  phoneDigits: string;
  acceptTerms: boolean;
};

export default function CadastroCelular() {
  const navigate = useNavigate();
  const { showError } = useErrorDialog();

  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [captchaToken, setCaptchaToken] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const captchaRef = useRef<RecaptchaCheckboxHandle>(null);

  const applyCooldown = (seconds?: number) => {
    const s = typeof seconds === 'number' && seconds > 0 ? seconds : 60;
    setCooldown((cur) => Math.max(cur, s));
  };
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const [country, setCountry] = useState<Country>(COUNTRIES.find((c) => c.iso === 'br')!);
  const [form, setForm] = useState<RegisterForm>({
    name: '', loginId: '', password: '', confirmPassword: '', phoneDigits: '', acceptTerms: false,
  });

  const phoneMasked = formatPhone(country, form.phoneDigits);
  const phoneE164 = toE164(country, form.phoneDigits);
  const phoneValid = Boolean(phoneE164);
  const otpValid = otp.length === 6;

  const payload = useMemo(() => ({
    name: form.name.trim(),
    loginId: form.loginId.trim(),
    password: form.password,
    phoneDigits: onlyDigits(form.phoneDigits),
    acceptTerms: form.acceptTerms,
    phoneE164,
  }), [form, phoneE164]);

  const setField = <K extends keyof RegisterForm>(k: K, v: RegisterForm[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const validateFields = (): string | null => {
    const name = form.name.trim();
    const loginId = form.loginId.trim();
    const nameRegex = /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/;
    if (name.length < 3) return 'Informe nome (mín. 3 caracteres).';
    if (name.length > 30) return 'Informe nome (máx. 30 caracteres).';
    if (!nameRegex.test(name)) return 'Nome pode conter apenas letras, números e espaços (sem símbolos).';
    if (loginId.length < 4) return 'Informe um ID (mín. 4 caracteres).';
    if (loginId.length > 16) return 'Informe um ID (máx. 16 caracteres).';
    if (form.password.length < 6) return 'Senha muito curta (mín. 6 caracteres).';
    if (form.password !== form.confirmPassword) return 'As senhas não conferem.';
    if (!phoneValid) return 'Informe um celular válido.';
    if (!form.acceptTerms) return 'Você precisa aceitar os termos para continuar.';
    return null;
  };

  const doRequest = async (token: string) => {
    try {
      setLoading(true);
      const res: RegisterApiResponse = await createAccount({
        Username: payload.loginId,
        Password: payload.password,
        Name: payload.name,
        Cellphone: payload.phoneE164,
        Code: null,
        Captcha: token || null,
      } as any);
      if (res?.status === '1' || res?.Status === '1') {
        setStep('code'); setOtp(''); applyCooldown(res.retryAfterSeconds ?? 60);
        return;
      }
      if (res?.status === '3' || res?.Status === '3') { setStep('success'); return; }
      const msg = res?.message || res?.Message || 'Não foi possível enviar o código.';
      showError({ title: 'Falha ao enviar código', message: msg });
    } catch (e: any) {
      const msg = e?.Message || e?.message || 'Não foi possível enviar o código. Tente novamente.';
      if (typeof e?.retryAfterSeconds === 'number') applyCooldown(e.retryAfterSeconds);
      showError({ title: 'Falha ao enviar código', message: msg });
      captchaRef.current?.reset(); setCaptchaToken('');
    } finally { setLoading(false); }
  };

  const handleCaptchaResolved = (token: string) => {
    setCaptchaToken(token);
    if (showCaptcha) {
      setShowCaptcha(false);
      setTimeout(() => doRequest(token), 150);
    }
  };

  const requestCode = async () => {
    const err = validateFields();
    if (err) { showError({ title: 'Verifique os campos', message: err }); return; }
    if (isRecaptchaEnabled() && !captchaToken) {
      setShowCaptcha(true);
      return;
    }
    await doRequest(captchaToken);
  };

  const verifyCode = async () => {
    setError(null);
    if (!otpValid) { setError('Digite o código completo (6 dígitos).'); return; }
    try {
      setLoading(true);
      const resVerify: RegisterApiResponse = await createAccount({
        Username: payload.loginId, Password: payload.password, Name: payload.name,
        Cellphone: payload.phoneE164, Code: otp,
      } as any);
      if (resVerify?.status === '3' || resVerify?.Status === '3') { setStep('success'); return; }
      if (resVerify?.status !== '2' && resVerify?.Status !== '2') {
        const msg = resVerify?.message || resVerify?.Message || 'Código inválido. Tente novamente.';
        setError(msg); showError({ title: 'Falha na verificação', message: msg }); return;
      }
      const resCreate: RegisterApiResponse = await createAccount({
        Username: payload.loginId, Password: payload.password, Name: payload.name,
        Cellphone: payload.phoneE164, Code: null,
        ReferCode: (localStorage.getItem(REFERRAL_STORAGE_KEY) || '').trim() || null,
      } as any);
      if (resCreate?.status === '3' || resCreate?.Status === '3') {
        try { localStorage.removeItem(REFERRAL_STORAGE_KEY); } catch { /* */ }
        setStep('success'); return;
      }
      const msg = resCreate?.message || resCreate?.Message || 'Não foi possível concluir o cadastro.';
      setError(msg); showError({ title: 'Falha ao concluir', message: msg });
    } catch (e: any) {
      const msg = e?.Message || e?.message || 'Falha na verificação. Tente novamente.';
      if (typeof e?.retryAfterSeconds === 'number') applyCooldown(e.retryAfterSeconds);
      setError(msg); showError({ title: 'Falha na verificação', message: msg });
    } finally { setLoading(false); }
  };

  const resend = async () => { if (cooldown > 0) return; await requestCode(); };

  if (step === 'success') {
    return (
      <div>
        <div className="auth-note">
          <strong>Cadastro realizado com sucesso</strong>
          Dentro dos próximos dias você será notificado do lançamento de Cabal Liberty.
        </div>
        <a className="btn btn-silver submit-btn" href={WHATSAPP_URL} target="_blank" rel="noreferrer">
          Entrar no grupo do WhatsApp
        </a>
        <div className="auth-links center">
          <button type="button" onClick={() => navigate('/')}>Voltar para a home</button>
        </div>
      </div>
    );
  }

  if (step === 'code') {
    return (
      <div>
        <div className="auth-note">
          Enviamos um código para <b>{phoneE164}</b>. Digite abaixo.
        </div>
        <OtpInput value={otp} onChange={setOtp} disabled={loading} length={6} />
        <div className="auth-links">
          <button type="button" onClick={() => setStep('form')} disabled={loading}>← Voltar e editar</button>
          <button type="button" onClick={resend} disabled={loading || cooldown > 0}>
            {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
          </button>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <button type="button" className="btn btn-gold submit-btn" onClick={verifyCode} disabled={!otpValid || loading}>
          {loading ? 'Validando...' : 'Confirmar cadastro'}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); requestCode(); }}>
      <div className="form-row">
        <label>Primeiro Nome<input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Primeiro nome" /></label>
        <label>ID do Jogo<input value={form.loginId} onChange={(e) => setField('loginId', e.target.value)} placeholder="Seu ID (login)" /></label>
      </div>
      <div className="form-row">
        <label>Senha<input type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="••••••••" /></label>
        <label>Confirmar senha<input type="password" value={form.confirmPassword} onChange={(e) => setField('confirmPassword', e.target.value)} placeholder="••••••••" /></label>
      </div>
      <div className="field">
        Celular
        <div className="phone-row">
          <CountrySelector className="phone-country" value={country} onChange={(c) => { setCountry(c); setField('phoneDigits', ''); }} />
          <input
            value={phoneMasked}
            onChange={(e) => setField('phoneDigits', onlyDigits(e.target.value).slice(0, country.maxDigits))}
            inputMode="tel"
            placeholder={phonePlaceholder(country)}
          />
        </div>
        <small>Enviaremos um código de verificação.</small>
      </div>
      <TermsCheck checked={form.acceptTerms} onChange={(v) => setField('acceptTerms', v)} onOpenTerms={() => setShowTerms(true)} />
      <button className="btn btn-gold submit-btn" type="submit" disabled={loading}>
        {loading ? 'Enviando...' : 'Criar conta'}
      </button>

      <TermsDialog open={showTerms} onClose={() => setShowTerms(false)} />

      {showCaptcha && (
        <CaptchaModal loading={loading} text="Resolva o desafio abaixo para enviar o código." onClose={() => setShowCaptcha(false)}>
          <RecaptchaCheckbox ref={captchaRef} onChange={(t) => { if (t) handleCaptchaResolved(t); }} theme="dark" />
        </CaptchaModal>
      )}
    </form>
  );
}
